import { ExpertRequestFieldPermission } from '@/__generated__/graphql';

export type FieldGroupName =
  | 'content'
  | 'disclosure'
  | 'attachments'
  | 'questions'
  | 'qualifications';

/**
 * Reads the server-computed §5.5 editability matrix (ENG-5290 T2). The webapp
 * never re-derives the matrix — it renders read-only/editable purely from
 * `ExpertRequest.field_permissions`. Missing data falls back to editable so the
 * coarse `update` permission stays the gate for legacy/unfetched rows.
 */
export function canEditGroup(
  permissions:
    | readonly Pick<ExpertRequestFieldPermission, 'group' | 'can_edit'>[]
    | null
    | undefined,
  group: FieldGroupName
): boolean {
  if (!permissions) return true;
  const entry = permissions.find((p) => p.group === group);
  return entry ? entry.can_edit : true;
}

export function canHideReorderGroup(
  permissions:
    | readonly Pick<ExpertRequestFieldPermission, 'group' | 'can_hide_reorder'>[]
    | null
    | undefined,
  group: FieldGroupName
): boolean {
  if (!permissions) return false;
  const entry = permissions.find((p) => p.group === group);
  return entry ? entry.can_hide_reorder : false;
}

export type QueryRowPerm = 'edit' | 'hide_reorder' | 'read';

type QueryPermInput =
  | readonly Pick<ExpertRequestFieldPermission, 'group' | 'can_edit' | 'can_hide_reorder'>[]
  | null
  | undefined;

/**
 * Per-row editability for a vetting question/qualification (ENG-5292). A
 * new/unsaved row (no id) is always fully editable — the §5.5 matrix only
 * governs persisted rows. Existing rows: full edit, else hide+reorder only,
 * else read-only.
 */
export function queryRowPerm({
  permissions,
  group,
  hasId,
}: {
  permissions: QueryPermInput;
  group: FieldGroupName;
  hasId: boolean;
}): QueryRowPerm {
  if (!hasId) return 'edit';
  if (canEditGroup(permissions, group)) return 'edit';
  if (canHideReorderGroup(permissions, group)) return 'hide_reorder';
  return 'read';
}
